import { registerTenants } from './tenants/register';
import { HeadersFunction, json, LoaderFunctionArgs, redirect } from '@remix-run/cloudflare';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
} from '@remix-run/react';
import { TenantContextProvider } from './tenants/common/TenantContextProvider';
import { getTenant } from '~/tenants/common/registry';

import { tenant_require } from '~/tenants/common/util.server';
import { user_optional } from '~/lib/auth';
import { staff_email } from '~/lib/staff';
import { clientProviders } from '~/.server/client-providers';
import { db_tenant_read } from '~/.server/drizzle/repository';
import { PortalHolder } from '~/components/interactive/Portals';
import { IntlProvider } from 'react-intl';
import stylesheet from '~/tailwind.css?url';
import { useEffect } from 'react';
import { rainforest_is_production_resolved } from '~/.server/rainforest';
import { algolia_office_search } from '~/.server/algolia-search';
import { vendor_path } from '~/.server/vendor-guard';
import { root_should_revalidate } from '~/lib/root-revalidate';
import RouteErrorBoundary from '~/components/RouteErrorBoundary';
import { arrivalHours } from '~/lib/arrival-windows';
import * as Sentry from '@sentry/cloudflare';
import { applyDemoBrand, demoBrand } from '~/.server/demo/brand';
import { durable_browser_origin } from '~/lib/durable-origin';

registerTenants();

export const headers: HeadersFunction = () => {
  return {
    'Cache-Control': 'private, no-store',
  };
};

/**
 * Root data is per viewer (the office's search key, its providers' home
 * bases): no shared cache may keep it. `headers` above covers the document;
 * a `?_data=root` request answers with the loader's own headers, set here.
 */
const ROOT_DATA_HEADERS = { 'Cache-Control': 'private, no-store' };

export async function loader(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url);
  const trace = Sentry.getTraceData();

  if (args.context.env.IS_CLOUDFLARE && args.request.headers.get('X-Forwarded-Proto') !== 'https') {
    url.protocol = 'https';

    return redirect(url.toString(), { status: 301 });
  }

  const tenant = tenant_require(args);

  /*
   * THE CLIENT'S TENANT IS THE CODE REGISTRY, bundled into the browser JS —
   * `getTenant(tenantId)` below — and every client-side provider lookup
   * (`useTenant().provider(id)`: the schedule board's rows, names, zips)
   * reads from it. So the DB provider overlay that db_tenant_read applies on
   * the server never reached a screen: a provider created in Settings
   * resolved correctly in every loader and was still absent from the board,
   * because the board draws from the client map. Shipping the RESOLVED
   * providers here, and letting the `{ ...tenant, ...config }` spread in App
   * override the code map, is the only way the client can see them.
   *
   * Plain data (names, addresses, rates) — serialisable as-is. Read per
   * document load, form action and area change (shouldRevalidate below).
   */
  const resolved = await db_tenant_read(args);

  // Sales demo: its brand replaces the tenant's own name and logos in the
  // client's tenant (the `{ ...tenant, ...config }` spread below). Null and
  // absent everywhere else.
  const demo = applyDemoBrand({ name: tenant.name, domain: tenant.domain, logoRaster: tenant.logoRaster, logoVector: tenant.logoVector }, await demoBrand(args));
  const branded = demo.name !== tenant.name || demo.logoRaster !== tenant.logoRaster || demo.logoVector !== tenant.logoVector;
  const viewer = await user_optional(args).catch(() => null);
  // The marketing vocabulary is for the AGENT only: never the office or a photographer.
  const agentViewer = Boolean(viewer && !viewer.admin && !viewer.provider);

  // Office search: the OFFICE only, on its own pages only (/vendor/*, where
  // every search UI lives), and a key restricted to this tenant's indexes and
  // this user that expires in an hour — never ALGOLIA_SEARCH_KEY itself
  // (.server/algolia-search.ts). Until Oct 2026 the raw key went to every
  // viewer, signed out included, and the users/customers indexes hold names,
  // emails and phone numbers. Off /vendor — the customer portal an office user
  // also opens is recorded by Sentry Replay with network bodies — no key at
  // all; crossing into /vendor re-runs this loader (lib/root-revalidate.ts).
  const algolia =
    viewer && viewer.admin && vendor_path(url.pathname) ? { algolia: await algolia_office_search(args, viewer.id) } : {};

  return json(
    {
      trace,
      tenantId: tenant.id,
      tenantName: demo.name,
      ...(branded ? { name: demo.name, logoRaster: demo.logoRaster, logoVector: demo.logoVector, demoCopy: agentViewer } : {}),
      // The home base only for the office (clientProviders).
      providers: clientProviders(resolved.config.providers, Boolean(viewer?.admin), staff_email(viewer?.email) ? undefined : resolved.demoHidden),
      rainforest_production: await rainforest_is_production_resolved(args),
      ...algolia,
      // What "All day" means to this company's customers (lib/arrival-windows.ts).
      arrival: arrivalHours(resolved.data.company),
      // Where the inbox's sockets go: production's durable worker, staging's
      // own, or nowhere (lib/durable-origin.ts). Never guessed by the browser.
      durable: durable_browser_origin(args.context.env),
    },
    { headers: ROOT_DATA_HEADERS },
  );
}

/*
 * Re-read after any form action, and when a navigation crosses top-level
 * areas (sign-out, sign-in, office ↔ portal) — never on plain navigation
 * inside one area. Why each: lib/root-revalidate.ts.
 */
export const shouldRevalidate: ShouldRevalidateFunction = root_should_revalidate;

/**
 * The ROOT boundary replaces <App/> — and App is the <html> element. Render
 * the message without a document and React tries to append a <div> straight
 * to `document` ("Only one element on document allowed"), the boundary
 * itself throws, and the customer gets a blank white page in an error loop
 * — which is exactly what the first live voice-order try produced (4 Sep):
 * a small render bug became an invisible one. Same shell as App, minimal.
 */
export function ErrorBoundary() {
  return (
    <html lang="en" dir="ltr" className="bg-theme-body body">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <link rel="stylesheet" href={stylesheet} />
      </head>
      <body className="tracking-normal text-gray-900 body" id="root">
        <div className="twp">
          <RouteErrorBoundary />
        </div>
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  // `durable` is read by the inbox hooks (useDurableOrigin), not the tenant.
  const { trace, tenantId, rainforest_production, durable: _durable, ...config } = useLoaderData<typeof loader>();

  const tenant = getTenant(tenantId);

  useEffect(() => {
    if (typeof window !== 'undefined' && tenant.background) {
      document.documentElement.style.setProperty('--tenant', tenant.background);
    }
  }, []);

  return (
    <html lang="en" dir="ltr" className="bg-theme-body body">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=0" />
        <Meta />
        <Links />
        <link rel="stylesheet" href={stylesheet} />
        <link rel="apple-touch-icon" sizes="180x180" href={`/tenants/${tenantId}/favicon/apple-touch-icon.png`} />
        <link rel="icon" type="image/png" sizes="32x32" href={`/tenants/${tenantId}/favicon/favicon-32x32.png`} />
        <link rel="icon" type="image/png" sizes="16x16" href={`/tenants/${tenantId}/favicon/favicon-16x16.png`} />
        <link rel="manifest" href={`/tenants/${tenantId}/favicon/site.webmanifest`} />
        <link rel="mask-icon" href={`/tenants/${tenantId}/favicon/safari-pinned-tab.svg`} color={tenant.background} />
        <link rel="shortcut icon" href={`/tenants/${tenantId}/favicon/favicon.ico`} />
        <meta name="msapplication-TileColor" content={tenant.background} />
        <meta name="msapplication-config" content="/tenants/nep/favicon/browserconfig.xml" />
        {/*<meta name="theme-color" content="#f9fafb" />*/}
        {/*<meta name="theme-color" content={tenant.background} />*/}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        {rainforest_production ? (
          <>
            <script type="module" src="https://static.rainforestpay.com/payment.js" />
            <script type="module" src="https://static.rainforestpay.com/merchant.js" />
          </>
        ) : (
          <>
            <script type="module" src="https://static.rainforestpay.com/sandbox.payment.js" />
            <script type="module" src="https://static.rainforestpay.com/sandbox.merchant.js" />
          </>
        )}
        <meta name="sentry-trace" content={trace['sentry-trace']} />
        <meta name="baggage" content={trace.baggage} />
      </head>
      <body className="tracking-normal text-gray-900 body overflow-x-hidden" id="root">
        <IntlProvider locale="en">
          <TenantContextProvider tenant={{ ...tenant, ...config }}>
            <div className="twp">
              <Outlet />
            </div>
          </TenantContextProvider>
        </IntlProvider>
        <ScrollRestoration />
        <Scripts />
        <PortalHolder />
      </body>
    </html>
  );
}
